import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Button from '../../components/Button'
import { Pages } from '../../Pages'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import authApi from '../../api/auth/AuthApi'
import MagicLinkNotification from './MagicLinkNotification'
import { useAuth } from '../../auth/AuthContext'
import EmailLinkButton from './EmailLinkButton'
import { executeRecaptcha, preloadRecaptcha } from '../../utils/recaptcha'

export default function SignUp(): React.JSX.Element {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { refresh } = useAuth()

    const initialEmailRef = useRef(searchParams.get('email') ?? '')
    const methodParam = searchParams.get('method')
    const stepParam = searchParams.get('step')
    const initialStepRef = useRef<'email' | 'choose-method' | 'magic-sent'>(
        methodParam === 'magic-link'
            ? 'magic-sent'
            : stepParam === 'choose-method' && initialEmailRef.current
              ? 'choose-method'
              : 'email',
    )

    const [email, setEmail] = useState(initialEmailRef.current)
    const [status, setStatus] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(
        initialStepRef.current === 'magic-sent' && initialEmailRef.current
            ? `We have sent a magic link to ${initialEmailRef.current}`
            : null,
    )
    const [sendingLink, setSendingLink] = useState<boolean>(false)
    const [checkingEmail, setCheckingEmail] = useState(false)
    const [creatingPassword, setCreatingPassword] = useState(false)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [step, setStep] = useState<
        'email' | 'choose-method' | 'magic-sent' | 'existing-account'
    >(initialStepRef.current)
    const [existingAccountEmail, setExistingAccountEmail] = useState<
        string | null
    >(null)

    useEffect(() => {
        void preloadRecaptcha()
    }, [])

    const updateQuery = useCallback(
        (nextEmail: string, method?: 'magic-link') => {
            const params = new URLSearchParams()
            if (nextEmail) {
                params.set('email', nextEmail)
            }
            if (method) {
                params.set('method', method)
            }
            navigate(
                params.toString()
                    ? `${Pages.Signup}?${params.toString()}`
                    : Pages.Signup,
                { replace: true },
            )
        },
        [navigate],
    )

    const handleEmailChange = useCallback((value: string) => {
        setEmail(value)
        setStatus(null)
        setStep('email')
        setSuccessMessage(null)
        setPassword('')
        setConfirmPassword('')
        setExistingAccountEmail(null)
    }, [])

    const handleEmailSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            const trimmed = email.trim()
            if (!trimmed) {
                setStatus('Please enter your email before continuing.')
                setSuccessMessage(null)
                return
            }

            setEmail(trimmed)
            setStatus(null)
            setSuccessMessage(null)
            setPassword('')
            setConfirmPassword('')

            try {
                setCheckingEmail(true)
                const recaptchaToken = await executeRecaptcha('check_email')
                const response = await authApi.checkEmailExists(
                    trimmed,
                    recaptchaToken,
                )
                if (response.exists) {
                    setExistingAccountEmail(trimmed)
                    setStatus(null)
                    setStep('existing-account')
                } else {
                    setExistingAccountEmail(null)
                    setStep('choose-method')
                }
                updateQuery(trimmed)
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to verify this email. Please try again.'
                setStatus(
                    message.replace(
                        /^There was an issue communicating with the Contxt server:\s*/,
                        '',
                    ),
                )
                setStep('email')
            } finally {
                setCheckingEmail(false)
            }
        },
        [email, updateQuery],
    )

    const handleSendMagicLink = useCallback(async () => {
        const trimmed = email.trim()
        if (!trimmed) {
            setStatus('Please enter your email before continuing.')
            return
        }

        if (sendingLink) return

        try {
            setSendingLink(true)
            setSuccessMessage(null)
            const recaptchaToken = await executeRecaptcha('signup_magic_link')
            await authApi.signupMagicLink(trimmed, undefined, recaptchaToken)
            setStep('magic-sent')
            updateQuery(trimmed, 'magic-link')
            setStatus(null)
            setSuccessMessage(`We have sent a magic link to ${trimmed}`)
            setExistingAccountEmail(null)
        } catch (err: unknown) {
            if (err instanceof Error) {
                const cleanMessage = err.message.replace(
                    /^There was an issue communicating with the Contxt server:\s*/,
                    '',
                )
                if (/already exists/i.test(cleanMessage)) {
                    setExistingAccountEmail(trimmed)
                    setStatus(null)
                    setStep('existing-account')
                } else {
                    setStatus(cleanMessage)
                }
            } else {
                setStatus('Unable to send magic link. Please try again.')
            }
            setSuccessMessage(null)
        } finally {
            setSendingLink(false)
        }
    }, [email, sendingLink, updateQuery])

    const handleUseDifferentEmail = useCallback(() => {
        setStep('email')
        setStatus(null)
        setSuccessMessage(null)
        const trimmed = email.trim()
        updateQuery(trimmed)
        setPassword('')
        setConfirmPassword('')
        setCreatingPassword(false)
        setExistingAccountEmail(null)
    }, [email, updateQuery])

    const handleCreatePassword = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()

            const trimmedEmail = email.trim()
            if (!trimmedEmail) {
                setStatus('Please enter your email before continuing.')
                setStep('email')
                return
            }

            if (!password || !confirmPassword) {
                setStatus('Please fill out both password fields.')
                return
            }

            if (password.length < 8) {
                setStatus('Password must be at least 8 characters long.')
                return
            }

            if (!/^[A-Za-z0-9]+$/.test(password)) {
                setStatus('Password can only contain letters and numbers.')
                return
            }

            if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
                setStatus(
                    'Password must include at least one letter and one number.',
                )
                return
            }

            if (password !== confirmPassword) {
                setStatus('Passwords do not match.')
                return
            }

            try {
                setCreatingPassword(true)
                const recaptchaToken = await executeRecaptcha('password_signup')
                await authApi.signupWithPassword(
                    trimmedEmail,
                    password,
                    undefined,
                    recaptchaToken,
                )
                await refresh()
                navigate(Pages.SignupLinkedIn, { replace: true })
            } catch (err: unknown) {
                if (err instanceof Error) {
                    const cleanMessage = err.message.replace(
                        /^There was an issue communicating with the Contxt server:\s*/,
                        '',
                    )
                    if (/already exists/i.test(cleanMessage)) {
                        setExistingAccountEmail(trimmedEmail)
                        setStatus(null)
                        setStep('existing-account')
                    } else {
                        setStatus(cleanMessage)
                    }
                } else {
                    setStatus('Unable to create account. Please try again.')
                }
            } finally {
                setCreatingPassword(false)
            }
        },
        [email, password, confirmPassword, navigate, refresh],
    )

    const handleBack = useCallback(() => {
        navigate(Pages.Login)
    }, [navigate])

    if (step === 'magic-sent') {
        return (
            <MagicLinkNotification
                email={email}
                subtitle={
                    successMessage ||
                    `We sent a magic link to ${email}. Use it to finish signing up.`
                }
                note="You'll need to open the link from your inbox to continue."
                onUseDifferentEmail={handleUseDifferentEmail}
            />
        )
    }

    const showMarketing = step === 'email'

    return (
        <div
            className={
                showMarketing
                    ? 'flex flex-col md:flex-row min-h-screen items-center justify-center bg-gray-50 p-6 md:items-stretch md:justify-start md:bg-transparent md:p-0'
                    : 'min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6'
            }
        >
            {showMarketing && (
                <div className="hidden md:flex flex-1 bg-gradient-to-br from-ctxt-blue via-gray-900 to-gray-800 text-white flex-col justify-center items-center p-8">
                    <div className="max-w-md text-center space-y-4">
                        <img src="Contxt_Logo_Full_WHT.png" />
                        <h1 className="text-2xl md:text-2xl mb-2 opacity-90">
                            Top performers don't apply...
                        </h1>
                        <h1 className="text-3xl md:text-4xl font-bold opacity-95">
                            They move through trusted networks
                        </h1>
                    </div>
                </div>
            )}

            <div
                className={
                    showMarketing
                        ? 'w-full max-w-md mx-auto bg-white shadow-lg rounded-lg p-8 flex flex-col justify-center items-center md:max-w-none md:mx-0 md:flex-1 md:bg-white md:flex md:flex-col md:justify-center md:items-center md:p-6 md:shadow-none md:rounded-none'
                        : 'w-full max-w-md bg-white shadow-lg rounded-lg p-8'
                }
            >
                <div className="w-full max-w-sm space-y-6 text-center mx-auto">
                    {showMarketing && (
                        <div className="space-y-2 md:hidden">
                            <img
                                src="Contxt_Logo_Full_BLK_CROPPED.png"
                                className="w-40 mx-auto"
                                alt="Contxt"
                            />
                        </div>
                    )}
                    {!showMarketing && (
                        <div className="space-y-2">
                            <img
                                src="Contxt_Logo_Full_BLK_CROPPED.png"
                                className="w-40 mx-auto"
                                alt="Contxt"
                            />
                        </div>
                    )}

                    {step === 'email' && (
                        <form
                            className="space-y-4 text-center"
                            onSubmit={handleEmailSubmit}
                        >
                            <h2 className="text-2xl font-bold">Welcome!</h2>
                            <p className="text-gray-500">
                                Start by entering your email to continue.
                            </p>
                            <input
                                id="signup-email"
                                type="email"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(event) =>
                                    handleEmailChange(event.target.value)
                                }
                                required
                            />
                            <Button
                                style="primary"
                                type="submit"
                                classes="w-full"
                                enabled={!sendingLink && !checkingEmail}
                            >
                                Continue
                            </Button>
                        </form>
                    )}

                    {step === 'choose-method' && (
                        <div className="space-y-6 text-left">
                            <div className="space-y-1 text-center">
                                <p className="text-sm font-medium text-gray-700">
                                    Sign up with <strong>{email}</strong>
                                </p>
                            </div>

                            <EmailLinkButton
                                sendingLink={sendingLink}
                                handleSendMagicLink={handleSendMagicLink}
                            />

                            <div className="space-y-3 border-t border-gray-200 pt-4">
                                <form
                                    className="space-y-4"
                                    onSubmit={handleCreatePassword}
                                >
                                    <div className="space-y-1">
                                        <p className="text-sm font-semibold text-gray-800">
                                            Or create a password
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Use at least 8 characters with a mix
                                            of letters and numbers.
                                        </p>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type={
                                                showPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            className="w-full border border-gray-300 rounded px-3 py-2 pr-10"
                                            placeholder="Password"
                                            value={password}
                                            onChange={(event) => {
                                                setPassword(event.target.value)
                                                setStatus(null)
                                            }}
                                            minLength={8}
                                            pattern="[A-Za-z0-9]{8,}"
                                            title="Use at least 8 characters with letters and numbers."
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                                            onClick={() =>
                                                setShowPassword((prev) => !prev)
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    showPassword
                                                        ? faEyeSlash
                                                        : faEye
                                                }
                                            />
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type={
                                                showConfirmPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            className="w-full border border-gray-300 rounded px-3 py-2 pr-10"
                                            placeholder="Confirm password"
                                            value={confirmPassword}
                                            onChange={(event) => {
                                                setConfirmPassword(
                                                    event.target.value,
                                                )
                                                setStatus(null)
                                            }}
                                            minLength={8}
                                            pattern="[A-Za-z0-9]{8,}"
                                            title="Use at least 8 characters with letters and numbers."
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                                            onClick={() =>
                                                setShowConfirmPassword(
                                                    (prev) => !prev,
                                                )
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    showConfirmPassword
                                                        ? faEyeSlash
                                                        : faEye
                                                }
                                            />
                                        </button>
                                    </div>
                                    <Button
                                        style="secondary"
                                        type="submit"
                                        classes="w-full"
                                        enabled={!creatingPassword}
                                    >
                                        Create account
                                    </Button>
                                </form>
                            </div>

                            <div className="text-center">
                                <button
                                    type="button"
                                    className="text-sm text-blue-600 hover:underline"
                                    onClick={handleUseDifferentEmail}
                                >
                                    Use a different email
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'existing-account' && existingAccountEmail && (
                        <div className="space-y-4 text-left">
                            <p className="text-sm text-gray-600">
                                We already have an account for{' '}
                                <strong>{existingAccountEmail}</strong>.
                            </p>
                            <Button
                                style="primary"
                                type="button"
                                classes="w-full"
                                onClick={() =>
                                    navigate(
                                        `${
                                            Pages.Login
                                        }?email=${encodeURIComponent(
                                            existingAccountEmail,
                                        )}&step=choose-method`,
                                    )
                                }
                            >
                                Go to login
                            </Button>
                            <div className="text-center">
                                <button
                                    type="button"
                                    className="text-sm text-blue-600 hover:underline"
                                    onClick={handleUseDifferentEmail}
                                >
                                    Use a different email
                                </button>
                            </div>
                        </div>
                    )}

                    {status && (
                        <p className="text-sm text-red-600 whitespace-pre-wrap">
                            {status}
                        </p>
                    )}
                    {step !== 'choose-method' &&
                        step !== 'existing-account' && (
                            <button
                                type="button"
                                className="text-sm text-blue-600 hover:underline"
                                onClick={handleBack}
                            >
                                Back to login
                            </button>
                        )}
                </div>
            </div>
        </div>
    )
}
